import type { ReploProject } from "schemas/generated/project";

import * as React from "react";

import { Badge } from "@replo/design-system/components/badge/Badge";
import {
  SidebarMenuButton,
  useSidebar,
} from "@replo/design-system/components/sidebar/Sidebar";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import {
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
} from "@replo/design-system/components/tooltip/Tooltip";
import { useOverflowDetection } from "@replo/design-system/hooks/use-overflow-detection";
import twMerge from "@replo/design-system/utils/twMerge";
import LogoBadge from "@v2/components/LogoBadge";
import { getProjectName } from "@v2/utils/project-utils";
import { ChevronsUpDown, PencilRuler } from "lucide-react";

export const ProjectSelectorHeaderSkeleton = () => {
  const { open } = useSidebar();

  return <Skeleton className={twMerge("w-full", open ? "h-12" : "h-8")} />;
};

export const ProjectSelectorHeader = ({
  currentProject,
  workspaceName,
}: {
  currentProject?: ReploProject;
  workspaceName: string;
}) => {
  const isV2Project = currentProject
    ? currentProject.hostingType === "replo"
    : true;
  const projectName = currentProject
    ? getProjectName(currentProject)
    : workspaceName;
  const [projectNameOverflowRef, isProjectNameOverflowing] =
    useOverflowDetection();
  const [workspaceNameOverflowRef, isWorkspaceNameOverflowing] =
    useOverflowDetection();

  return (
    <SidebarMenuButton
      size="lg"
      className="data-popup-open:bg-menu-item-selected data-popup-open:text-default items-start"
    >
      <div className="flex gap-2 justify-center items-center h-full">
        <Badge size="xl">
          {isV2Project ? (
            <LogoBadge className="size-8!" />
          ) : (
            <PencilRuler className="w-full h-full" />
          )}
        </Badge>

        <div className="grid flex-1 text-left text-sm leading-tight">
          <TooltipRoot
            disabled={!isProjectNameOverflowing}
            disableHoverablePopup
          >
            <TooltipTrigger
              className="min-w-0 w-full"
              UNSAFE_className="block text-left"
            >
              <span
                ref={projectNameOverflowRef}
                className="truncate header-h6 line-clamp-1 truncate min-w-0 block"
              >
                {projectName}
              </span>
            </TooltipTrigger>
            <TooltipContent side="right">{projectName}</TooltipContent>
          </TooltipRoot>

          <TooltipRoot
            disabled={!isWorkspaceNameOverflowing}
            disableHoverablePopup
          >
            <TooltipTrigger
              className="min-w-0 w-full"
              UNSAFE_className="block text-left"
            >
              <span
                ref={workspaceNameOverflowRef}
                className="truncate text-xs text-muted"
              >
                {workspaceName}
              </span>
            </TooltipTrigger>
            <TooltipContent side="right">{workspaceName}</TooltipContent>
          </TooltipRoot>
        </div>
      </div>
      <ChevronsUpDown className="ml-auto size-3! mt-1" />
    </SidebarMenuButton>
  );
};
